import { ordersApi } from '../services/api';

// Transform API data to match frontend format
const transformOrderFromApi = (apiOrder) => {
    return {
        ...apiOrder,
        // Convert database fields to frontend format
        orderDate: apiOrder.order_date,
        deliveryDate: apiOrder.delivery_date,
        patientNameCode: apiOrder.patient_name_code,
        clientInfo: {
            name: apiOrder.client_name,
            clinic: apiOrder.client_clinic,
            phone: apiOrder.client_phone,
            email: apiOrder.client_email,
            address: apiOrder.client_address
        },
        // Works array is already formatted from SQL query
        works: apiOrder.works || [],
        files: apiOrder.files || [],
        discussion: apiOrder.discussion || { messages: [], lastActivity: null }
    };
};

// Transform frontend data to API format
const transformOrderToApi = (frontendOrder) => {
    return {
        clientInfo: frontendOrder.clientInfo,
        billingAddress: frontendOrder.billingAddress,
        saveBillingAddress: frontendOrder.saveBillingAddress,
        prosthetistId: frontendOrder.prosthetistId,
        patientNameCode: frontendOrder.patientNameCode,
        deliveryDate: frontendOrder.deliveryDate,
        works: frontendOrder.works || [],
        files: frontendOrder.files || []
    };
};

// Fonction pour charger les commandes depuis l'API avec filtres optionnels
export const loadOrders = async (filters = {}) => {
    try {
        const orders = await ordersApi.getAll(filters);
        return orders.map(transformOrderFromApi);
    } catch (error) {
        console.error('Erreur lors du chargement des commandes:', error);
        throw error;
    }
};

// Fonction pour obtenir toutes les commandes non supprimées
export const getActiveOrders = async (filters = {}) => {
    return await loadOrders(filters);
};

// Fonction pour ajouter une nouvelle commande
export const addOrder = async (order) => {
    try {
        const apiData = transformOrderToApi(order);
        const newOrder = await ordersApi.create(apiData);
        return transformOrderFromApi(newOrder);
    } catch (error) {
        console.error('Erreur lors de la création de la commande:', error);
        throw error;
    }
};

// Fonction pour mettre à jour le statut d'une commande
export const updateOrderStatus = async (orderId, newStatus) => {
    try {
        const updatedOrder = await ordersApi.updateStatus(orderId, newStatus);
        // Si la commande est supprimée, l'API renvoie null
        return updatedOrder ? transformOrderFromApi(updatedOrder) : null;
    } catch (error) {
        console.error('Erreur lors de la mise à jour du statut:', error);
        throw error;
    }
};

// Fonction pour mettre à jour une commande (édition)
export const updateOrder = async (orderId, orderData) => {
    try {
        const updatedOrder = await ordersApi.update(orderId, orderData);
        return transformOrderFromApi(updatedOrder);
    } catch (error) {
        console.error('Erreur lors de la modification de la commande:', error);
        throw error;
    }
};

// Fonction pour supprimer une commande
export const deleteOrder = async (orderId) => {
    try {
        await ordersApi.delete(orderId);
        return true;
    } catch (error) {
        console.error('Erreur lors de la suppression de la commande:', error);
        throw error;
    }
};

// Fonction pour ajouter un message à la discussion d'une commande
export const addMessageToOrder = async (orderId, message) => {
    try {
        const newMessage = await ordersApi.addMessage(orderId, message);
        return {
            id: newMessage.id,
            senderType: newMessage.sender_type,
            senderName: newMessage.sender_name,
            message: newMessage.message_text,
            timestamp: newMessage.created_at,
            isRead: newMessage.is_read
        };
    } catch (error) {
        console.error('Erreur lors de l\'ajout du message:', error);
        throw error;
    }
};

// Fonction pour marquer les messages comme lus
export const markMessagesAsRead = async (orderId, userType) => {
    try {
        await ordersApi.markMessagesAsRead(orderId, userType);
        return true;
    } catch (error) {
        console.error('Erreur lors de la mise à jour des messages:', error);
        throw error;
    }
};

// Fonction pour obtenir le nombre de messages non lus pour une commande
export const getUnreadMessagesCount = async (orderId, userType) => {
    try {
        return await ordersApi.getUnreadMessagesCount(orderId, userType);
    } catch (error) {
        console.error('Erreur lors de la récupération des messages non lus:', error);
        return 0;
    }
};

// Fonction pour initialiser les données (maintenant géré par la base de données)
export const initializeSampleData = async () => {
    try {
        return await loadOrders();
    } catch (error) {
        console.error('Erreur lors de l\'initialisation des données:', error);
        return [];
    }
};

